import React, { useState, useContext, useMemo } from "react";
import { Button, Spinner, Alert } from "react-bootstrap";
import { FaLayerGroup, FaLock, FaMagic } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api";
import { AuthContext } from "../contexts/AuthContext";

const SmartPDFButton = ({
  selectedQuestions,
  worksheetInfo,
  settings,
  disabled = false,
  size = "sm",
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { auth } = useContext(AuthContext);
  const user = auth?.user;

  // Check if user has access to Smart PDF feature
  const hasAccess = useMemo(() => {
    console.log("🚀 Smart PDF Access Check - FRONTEND VERSION:", {
      user: user,
      is_staff: user?.is_staff,
      is_superuser: user?.is_superuser,
      subscription: user?.subscription,
      usersubscription: user?.usersubscription,
      auth_object: auth,
    });

    if (!user) return false;
    if (user.is_staff || user.is_superuser) return true;

    // Check subscription status - handle both potential field names
    const subscription = user.subscription || user.usersubscription;
    if (!subscription) return false;

    return subscription.has_active_access || subscription.is_in_trial;
  }, [user, auth]);

  // Check if we have valid questions selected
  const hasValidQuestions = useMemo(() => {
    return selectedQuestions && selectedQuestions.length > 0;
  }, [selectedQuestions]);

  const generatePDF = async () => {
    if (!hasValidQuestions) {
      toast.error("Please select questions before generating Smart PDF");
      return;
    }

    setIsGenerating(true);

    try {
      // Extract reference codes from selected questions
      const questionCodes = selectedQuestions
        .map((question) => {
          // Handle different question object structures
          return question.reference_code || question.id || question.tempId;
        })
        .filter((code) => code); // Remove any undefined/null codes

      if (questionCodes.length === 0) {
        throw new Error("No valid question codes found");
      }

      // Prepare worksheet info with defaults
      const worksheetData = {
        title:
          worksheetInfo?.title ||
          `${worksheetInfo?.subject || "Mixed"} Worksheet`,
        subject: worksheetInfo?.subject || "Mixed Subject",
        topic: worksheetInfo?.topic || "",
        difficulty: worksheetInfo?.difficulty || "",
        paper: worksheetInfo?.paper || "",
      };

      console.log("Generating Smart PDF with data:", {
        questionCount: questionCodes.length,
        worksheetData,
        settings,
      });

      // Make API request
      const response = await api.post(
        "/api/smart-pdf/generate/",
        {
          question_codes: questionCodes,
          worksheet_info: worksheetData,
          settings: settings || {},
        },
        {
          responseType: "blob",
          timeout: 30000, // 30 second timeout for PDF generation
        }
      );

      // Create and download the PDF
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename
      const filename = `Smart_Worksheet_${worksheetData.subject.replace(
        /\s+/g,
        "_"
      )}_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(
        `Smart PDF generated successfully! (${selectedQuestions.length} questions)`
      );
    } catch (error) {
      console.error("Smart PDF generation error:", error);

      let errorMessage = "Failed to generate Smart PDF. Please try again.";

      if (error.response) {
        // Handle API error responses
        if (error.response.status === 403) {
          errorMessage =
            error.response.data?.error ||
            "Smart PDF requires an active subscription";
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.error || "Invalid request data";
        } else if (error.response.status === 500) {
          errorMessage =
            "Server error during PDF generation. Please try again later.";
        }
      } else if (error.code === "ECONNABORTED") {
        errorMessage =
          "PDF generation timed out. Please try with fewer questions.";
      }

      toast.error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  // Render different button states based on access and conditions
  if (!hasAccess) {
    return (
      <Button
        variant="outline-primary"
        size={size}
        disabled
        className="d-flex align-items-center gap-1"
      >
        <FaLock />
        Smart PDF (Premium) [FRONTEND]
      </Button>
    );
  }

  if (!hasValidQuestions) {
    return (
      <Button
        variant="outline-secondary"
        size={size}
        disabled
        className="d-flex align-items-center gap-1"
      >
        <FaLayerGroup />
        Smart PDF (Select Questions)
      </Button>
    );
  }

  return (
    <Button
      variant="primary"
      size={size}
      onClick={generatePDF}
      disabled={disabled || isGenerating}
      className="d-flex align-items-center gap-1"
    >
      {isGenerating ? (
        <>
          <Spinner size="sm" />
          Generating...
        </>
      ) : (
        <>
          <FaMagic />
          Smart PDF ({selectedQuestions.length})
        </>
      )}
    </Button>
  );
};

export default SmartPDFButton;
